/**
 * E1b engine context.
 *
 * VITE_ENGINE controls the adapter:
 *   mock  (default) — MockEngineAdapter, no API server required
 *   http            — HttpEngineAdapter, proxies to /api
 *
 * Provides:
 *   useEngineAdapter()  — raw IEngineAdapter
 *   useEngineMeta()     — cached EngineMeta (loaded once on mount)
 *   useCapabilities()   — EngineCapability[] from meta
 *   incompatible        — true when contractVersion doesn't match CLIENT_CONTRACT_VERSION
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { IEngineAdapter } from './IEngineAdapter.interface'
import { MockEngineAdapter } from './MockEngineAdapter'
import { HttpEngineAdapter } from './HttpEngineAdapter'
import type { EngineMeta, EngineCapability } from '../../types/engine.types'

// ── Contract version this client was built against ────────────────────────────

const CLIENT_CONTRACT_VERSION = '1.2.0'

// ── Adapter resolution ────────────────────────────────────────────────────────

function resolveAdapter(): IEngineAdapter {
  const key = (import.meta.env.VITE_ENGINE as string | undefined) ?? 'mock'
  switch (key) {
    case 'http': return new HttpEngineAdapter()
    case 'mock':
    default:     return new MockEngineAdapter()
  }
}

const defaultAdapter = resolveAdapter()

// ── Context shape ─────────────────────────────────────────────────────────────

interface EngineContextValue {
  adapter:      IEngineAdapter
  meta:         EngineMeta | null
  incompatible: boolean
}

const EngineContext = createContext<EngineContextValue>({
  adapter:      defaultAdapter,
  meta:         null,
  incompatible: false,
})

// ── Provider ──────────────────────────────────────────────────────────────────

interface EngineProviderProps {
  children: ReactNode
  /** Override the adapter — used in tests. */
  adapter?: IEngineAdapter
}

export function EngineProvider({ children, adapter: override }: EngineProviderProps) {
  const adapter = override ?? defaultAdapter

  const [meta, setMeta]               = useState<EngineMeta | null>(null)
  const [incompatible, setIncompat]   = useState(false)

  const loadMeta = useCallback(async () => {
    try {
      const m = await adapter.getMeta()
      setMeta(m)
      setIncompat(m.contractVersion !== CLIENT_CONTRACT_VERSION)
    } catch {
      // Non-fatal — meta unavailable in offline / test scenarios
    }
  }, [adapter])

  useEffect(() => {
    void loadMeta()
  }, [loadMeta])

  return (
    <EngineContext.Provider value={{ adapter, meta, incompatible }}>
      {children}
    </EngineContext.Provider>
  )
}

// ── Consumer hooks ─────────────────────────────────────────────────────────────

export function useEngineAdapter(): IEngineAdapter {
  return useContext(EngineContext).adapter
}

export function useEngineMeta(): EngineMeta | null {
  return useContext(EngineContext).meta
}

export function useCapabilities(): EngineCapability[] {
  return useContext(EngineContext).meta?.capabilities ?? []
}

export function useEngineIncompatible(): boolean {
  return useContext(EngineContext).incompatible
}
